import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { CaretRight, CaretDown, Envelope, Warning } from '@phosphor-icons/react';
import Layout from '../components/Layout';
import { StatementHistoryDialog } from '../components/statementTracking/StatementHistoryDialog';
import { formatDateDMY } from '../utils/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OVERDUE_DAYS = 30;
const DAY = 86400000;

const classify = (r) => {
  if (!r.last_sent_at) return { level: 0, days: null, label: 'Δεν έχει σταλεί ποτέ', cls: 'bg-red-50 text-red-700 border-red-200' };
  const days = Math.floor((Date.now() - new Date(r.last_sent_at).getTime()) / DAY);
  if (days > OVERDUE_DAYS) return { level: 1, days, label: `Εκπρόθεσμη (${days} ημ.)`, cls: 'bg-amber-50 text-amber-800 border-amber-200' };
  return { level: 2, days, label: 'Ενημερωμένη', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
};
const aptKey = (a) => [a.st.level, a.last_sent_at || ''];
const cmp = (x, y) => (x[0] - y[0]) || String(x[1]).localeCompare(String(y[1]));

const AptRow = ({ a, onOpen }) => (
  <tr className="hover:bg-zinc-50 cursor-pointer" onClick={() => onOpen(a)} data-testid={`st-apt-row-${a.apartment_id}`}>
    <td className="pl-12 pr-2 py-1.5 font-mono">Διαμέρισμα {a.apartment_number || '—'}</td>
    <td className="px-2 py-1.5 truncate max-w-[240px]">{a.owner_name || '—'}</td>
    <td className="px-2 py-1.5" data-testid={`st-apt-lastsent-${a.apartment_id}`}>
      {a.last_sent_at ? <span className="font-mono">{formatDateDMY(String(a.last_sent_at).slice(0, 10))}</span> : <span className="italic text-red-600 inline-flex items-center gap-1"><Warning size={12} />Δεν έχει καταγραφεί</span>}
    </td>
    <td className="px-2 py-1.5 font-mono text-zinc-500">{a.sent_count || 0}</td>
    <td className="px-2 py-1.5"><span className={`text-[10px] font-semibold px-1.5 py-0.5 border ${a.st.cls}`} data-testid={`st-apt-status-${a.apartment_id}`}>{a.st.label}</span></td>
  </tr>
);

const BuildingBlock = ({ b, open, onToggle, onOpen }) => (
  <>
    <tr className={`cursor-pointer ${b.worst === 0 ? 'bg-red-50/40' : b.worst === 1 ? 'bg-amber-50/40' : ''} hover:bg-zinc-100`} onClick={onToggle} data-testid={`st-building-row-${b.building_id}`}>
      <td className="px-4 py-2 font-semibold" colSpan={2}>
        <span className="inline-flex items-center gap-2">
          {b.apts.length ? (open ? <CaretDown size={12} /> : <CaretRight size={12} />) : <span className="w-3" />}
          <Link to={`/buildings/${b.building_id}`} onClick={(e) => e.stopPropagation()} className="hover:underline">{b.building_name}</Link>
        </span>
      </td>
      <td className="px-2 py-2 font-mono text-zinc-600">{b.oldest ? formatDateDMY(String(b.oldest).slice(0, 10)) : (b.apts.length ? '—' : '')}</td>
      <td className="px-2 py-2 text-zinc-500">{b.apts.length} διαμ.</td>
      <td className="px-2 py-2 space-x-1 whitespace-nowrap" data-testid={`st-building-counts-${b.building_id}`}>
        {b.counts[0] > 0 && <span className="text-[10px] font-semibold px-1.5 py-0.5 border bg-red-50 text-red-700 border-red-200">{b.counts[0]} ποτέ</span>}
        {b.counts[1] > 0 && <span className="text-[10px] font-semibold px-1.5 py-0.5 border bg-amber-50 text-amber-800 border-amber-200">{b.counts[1]} εκπρόθεσμες</span>}
        {b.counts[2] > 0 && <span className="text-[10px] font-semibold px-1.5 py-0.5 border bg-emerald-50 text-emerald-700 border-emerald-200">{b.counts[2]} ενημερωμένες</span>}
        {!b.apts.length && <span className="text-zinc-400 italic text-[11px]">Χωρίς διαμερίσματα</span>}
      </td>
    </tr>
    {open && b.apts.map((a) => <AptRow key={a.apartment_id} a={a} onOpen={onOpen} />)}
  </>
);

export default function StatementTracking() {
  const [rows, setRows] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [q, setQ] = useState('');
  const [historyApt, setHistoryApt] = useState(null);

  useEffect(() => {
    axios.get(`${API}/dashboard/statement-tracking`, { withCredentials: true }).then((r) => setRows(r.data)).catch(() => setRows([]));
    axios.get(`${API}/buildings`, { withCredentials: true }).then((r) => setBuildings(r.data || [])).catch(() => {});
  }, []);

  const groups = useMemo(() => {
    const map = {};
    buildings.filter((b) => b.building_type !== 'cleaning_only' && !b.is_deleted)
      .forEach((b) => { map[b.building_id] = { building_id: b.building_id, building_name: b.name, apts: [] }; });
    (rows || []).forEach((r) => {
      if (!map[r.building_id]) return;
      map[r.building_id].apts.push({ ...r, st: classify(r) });
    });
    const list = Object.values(map).map((b) => {
      b.apts.sort((x, y) => cmp(aptKey(x), aptKey(y)) || String(x.apartment_number).localeCompare(String(y.apartment_number), undefined, { numeric: true }));
      b.counts = [0, 1, 2].map((l) => b.apts.filter((a) => a.st.level === l).length);
      b.worst = b.apts.length ? b.apts[0].st.level : 3;
      b.oldest = b.apts.length ? b.apts[0].last_sent_at : null;
      return b;
    });
    list.sort((x, y) => cmp([x.worst, x.oldest || ''], [y.worst, y.oldest || '']) || x.building_name.localeCompare(y.building_name));
    const qq = q.trim().toLowerCase();
    return qq ? list.filter((b) => b.building_name.toLowerCase().includes(qq)) : list;
  }, [rows, buildings, q]);

  const totals = [0, 1, 2].map((l) => groups.reduce((s, b) => s + b.counts[l], 0));
  const allOpen = groups.length > 0 && groups.every((b) => expanded[b.building_id]);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-8" data-testid="statement-tracking-page">
        <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}><Envelope size={22} />Statement Tracking</h1>
        <p className="text-sm text-zinc-500 mt-1">Βάσει της πραγματικής ημερομηνίας αποστολής. Εκπρόθεσμη = πάνω από {OVERDUE_DAYS} ημέρες ή καμία αποστολή. Τα παλαιότερα εμφανίζονται πρώτα.</p>
        <div className="flex flex-wrap items-center gap-2 mt-5 mb-3">
          <span className="text-xs font-semibold px-2 py-1 border bg-red-50 text-red-700 border-red-200" data-testid="st-total-never">{totals[0]} δεν έχουν σταλεί ποτέ</span>
          <span className="text-xs font-semibold px-2 py-1 border bg-amber-50 text-amber-800 border-amber-200" data-testid="st-total-overdue">{totals[1]} εκπρόθεσμες</span>
          <span className="text-xs font-semibold px-2 py-1 border bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="st-total-ok">{totals[2]} ενημερωμένες</span>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Αναζήτηση κτιρίου…" className="ml-auto border border-zinc-200 px-2 py-1 text-xs w-56 focus:outline-none focus:border-zinc-500" data-testid="st-search-input" />
          <button type="button" onClick={() => setExpanded(allOpen ? {} : Object.fromEntries(groups.map((b) => [b.building_id, true])))} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-zinc-300 hover:bg-zinc-50" data-testid="st-expand-all">{allOpen ? 'Σύμπτυξη όλων' : 'Ανάπτυξη όλων'}</button>
        </div>
        <div className="bg-white border border-zinc-200">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-600 text-left">
              <tr><th className="px-4 py-2" colSpan={2}>Κτίριο / Διαμέρισμα · Ιδιοκτήτης</th><th className="px-2 py-2">Τελευταία Αποστολή</th><th className="px-2 py-2">Αποστολές</th><th className="px-2 py-2">Κατάσταση</th></tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {rows === null && <tr><td colSpan={5} className="px-4 py-6 text-center text-zinc-400">Φόρτωση…</td></tr>}
              {groups.map((b) => (
                <BuildingBlock key={b.building_id} b={b} open={!!expanded[b.building_id]} onOpen={setHistoryApt}
                  onToggle={() => setExpanded((e) => ({ ...e, [b.building_id]: !e[b.building_id] }))} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <StatementHistoryDialog apt={historyApt} onClose={() => setHistoryApt(null)} />
    </Layout>
  );
}
